import {
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Table,
  Tooltip,
  Form,
  notification,
  PageHeader,
  Row,
  Col,
  InputNumber,
  Tag,
  Input,
  Select,
  DatePicker,
} from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import IconNotFound from "../../static/icon/IconNotFound";
import DeleteAction from "../../Tools/DeleteAction";

import { useAuth } from "../../context/auth";
import MerchantDropdown from "../Dropdown/MerchantDropdown";
import LandlordDropdown from "../Dropdown/LandlordDropdown";
import { calcPrice, formatBookingStatus, formatDateTime, formatPaymentMethod, formatSecond, listBookingStatus, listPaymentMethod } from "../../utils/formatter";
import Swal from "sweetalert2";
import discardChanges from "../../static/images/svgs/icon/discardChanges.svg";
import IconBookingManagement from "../../static/icon/IconBookingManagement";
import moment from "moment";

const Booking = () => {
  const history = useHistory();
  const { accessToken, adminRole } = useAuth();

  const [form] = Form.useForm();
  const selectedMerchants = Form.useWatch('merchantIds', form);

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [search, setSearch] = useState({
    pageValue: history.location.pageValue ?? {},
    searchValue: history.location.searchValue ?? {
      createdAfter: moment().startOf('day'),
      createdBefore: moment().endOf('day'),
    },
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [currentBooking, setCurrentBooking] = useState(null);

  useEffect(() => {
    async function getBookings({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/booking', window.location);
      if (searchValue.bookingId) url.searchParams.append('bookingId', searchValue.bookingId);
      if (searchValue.chargerId) url.searchParams.append('chargerId', searchValue.chargerId);
      if (searchValue.connectorId) url.searchParams.append('connectorId', searchValue.connectorId);
      if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);
      if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
      if (searchValue.userName) url.searchParams.append('userName', searchValue.userName);
      if (searchValue.status) url.searchParams.append('status', searchValue.status);
      if (searchValue.createdAfter) url.searchParams.append('createdAfter', searchValue.createdAfter.startOf('day').toISOString());
      if (searchValue.createdBefore) url.searchParams.append('createdBefore', searchValue.createdBefore.endOf('day').toISOString());
      if (searchValue.paymentMethod) url.searchParams.append('paymentMethod', searchValue.paymentMethod);

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);
      if (pageValue.sort) url.searchParams.append('sort', pageValue.sort);
      if (pageValue.order) url.searchParams.append('order', pageValue.order);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, bookings } = await res.json();
        setTotal(total);
        setBookings(bookings);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getBookings(search);
  }, [search, accessToken, update]);

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Name",
      dataIndex: "firstName",
      key: "firstName",
    },
    {
      title: "Surname",
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "tel",
      key: "tel",
    },
    {
      title: "Cluster Name",
      dataIndex: "merchantNameEn",
      key: "merchantNameEn",
    },
    {
      title: "Site Name",
      dataIndex: "landlordNameEn",
      key: "landlordNameEn",
    },
    {
      title: "Charger Name",
      dataIndex: "chargerNameEn",
      key: "chargerNameEn",
    },
    {
      title: "Connector Name",
      dataIndex: "connectorNameEn",
      key: "connectorNameEn",
    },
    {
      title: "Service Date / Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => formatDateTime(new Date(createdAt)),
    },
    {
      title: "Time",
      dataIndex: "stopTime",
      key: "stopTime",
      render: (_, booking) => formatSecond((new Date(booking.stopTime).getTime() - new Date(booking.startTime).getTime()) / 1000),
    },
    {
      title: "Energy (kWh)",
      dataIndex: "totalEnergyKwh",
      key: "totalEnergyKwh",
      render: (totalEnergyKwh) => `${totalEnergyKwh ?? 0} kWh`,
    },
    {
      title: "Total Amount",
      dataIndex: "adminAmount",
      key: "adminAmount",
      render: (_, booking) => {
        let result = calcPrice({
          booking: {
            ...booking,
            startTime: booking.startTime && new Date(booking.startTime),
            stopTime: booking.stopTime && new Date(booking.stopTime),
            avaliableAt: booking.avaliableAt && new Date(booking.avaliableAt),
            fullAt: booking.fullAt && new Date(booking.fullAt),
          },
          now: new Date(),
        });
        return (
          <Tooltip title={(
            <>
              <div>{result.chargedSecond} seconds {result.totalEnergyKwh} kWh</div>
              <div className="aria-readonly:text-red-600" aria-readonly={result.adminPriceByDuration}>
                THB {result.priceByDuration.toFixed(2)} by duration
              </div>
              <div className="aria-readonly:text-red-600" aria-readonly={result.adminPriceByPower}>
                THB {result.priceByPower.toFixed(2)} by energy
              </div>
              <div className="aria-readonly:text-red-600" aria-readonly={result.adminPriceByPenalty}>
                THB {result.priceByPenalty.toFixed(2)} by penalty
              </div>
            </>
          )}>
            <div className="aria-readonly:text-red-600" aria-readonly={result.adminPrice}>
              THB {result.price.toFixed(2)}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (paymentMethod) => formatPaymentMethod(paymentMethod),
    },
    {
      title: "Booking Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const { text, color } = formatBookingStatus(status);
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, booking) => (
        <span class="whitespace-nowrap flex">
          {(booking.status === 'created' || booking.status === 'started' || (['admin'].includes(adminRole) && booking.status === 'charging')) && (
            <Button
              size="small"
              type="link"
              danger
              onClick={async () => {
                const result = await Swal.fire({
                  title: `Delete booking`,
                  text: 'This will remove booking. This can\'t be undone.',
                  imageUrl: discardChanges,
                  showCancelButton: true,
                  confirmButtonText: "Confirm",
                  showLoaderOnConfirm: true,
                  reverseButtons: true,
                  buttonsStyling: false,
                  customClass: {
                    popup: "sw-popup",
                    htmlContainer: "sw-content",
                    confirmButton: "sw-btn-default sw-btn-confirm",
                    cancelButton: "sw-btn-default sw-btn-cancel",
                  },
                });
                if (!result.isConfirmed) return;

                const url = new URL(`/api/booking/${booking.id}`, window.location);
                const res = await fetch(url, {
                  headers: { 'X-ACCESS-TOKEN': accessToken },
                  method: 'DELETE',
                });
                if (res.ok) {
                  Swal.fire({
                    icon: "success",
                    title: "Deleted successfully!",
                    showConfirmButton: false,
                    timer: 1500,
                  });
                  setUpdate(update => update + 1);
                } else {
                  notification["error"]({
                    message: "Something went wrong !",
                    description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
                  });
                }
              }}
            >
              Delete
            </Button>
          )}
          {(booking.status === 'charging') && (
            <Button
              size="small"
              type="link"
              danger
              onClick={async () => {
                const result = await Swal.fire({
                  title: `Send remote stop charging`,
                  text: 'This will sent remote stop to charger.',
                  imageUrl: discardChanges,
                  showCancelButton: true,
                  confirmButtonText: "Confirm",
                  showLoaderOnConfirm: true,
                  reverseButtons: true,
                  buttonsStyling: false,
                  customClass: {
                    popup: "sw-popup",
                    htmlContainer: "sw-content",
                    confirmButton: "sw-btn-default sw-btn-confirm",
                    cancelButton: "sw-btn-default sw-btn-cancel",
                  },
                });
                if (!result.isConfirmed) return;

                const url = new URL(`/api/booking/${booking.id}/stop`, window.location);
                const res = await fetch(url, {
                  headers: { 'X-ACCESS-TOKEN': accessToken },
                  method: 'POST',
                });
                if (res.ok) {
                  Swal.fire({
                    icon: "success",
                    title: "Sent remote stop charging successfully!",
                    showConfirmButton: false,
                    timer: 1500,
                  });
                  setUpdate(update => update + 1);
                } else {
                  notification["error"]({
                    message: "Something went wrong !",
                    description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
                  });
                }
              }}
            >
              Stop
            </Button>
          )}
          {(['admin', 'callcenter'].includes(adminRole) && booking.status === 'charging') && (
            <Button
              size="small"
              type="link"
              danger
              onClick={async () => {
                const result = await Swal.fire({
                  title: `Force stop charging`,
                  text: 'This will change this booking to payment.',
                  imageUrl: discardChanges,
                  showCancelButton: true,
                  confirmButtonText: "Force stop",
                  showLoaderOnConfirm: true,
                  reverseButtons: true,
                  buttonsStyling: false,
                  customClass: {
                    popup: "sw-popup",
                    htmlContainer: "sw-content",
                    confirmButton: "sw-btn-default sw-btn-confirm",
                    cancelButton: "sw-btn-default sw-btn-cancel",
                  },
                });
                if (!result.isConfirmed) return;

                const url = new URL(`/api/booking/${booking.id}/force-stop`, window.location);
                const res = await fetch(url, {
                  headers: { 'X-ACCESS-TOKEN': accessToken },
                  method: 'POST',
                });
                if (res.ok) {
                  Swal.fire({
                    icon: "success",
                    title: "Force stopped successfully!",
                    showConfirmButton: false,
                    timer: 1500,
                  });
                  setUpdate(update => update + 1);
                } else {
                  notification["error"]({
                    message: "Something went wrong !",
                    description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
                  });
                }
              }}
            >
              Force Stop
            </Button>
          )}
          {(booking.status === 'charged') && (
            <Button
              size="small"
              type="link"
              danger
              onClick={async () => {
                const result = await Swal.fire({
                  title: `Remove cable`,
                  text: 'Are you sure to skip the cable removal?',
                  imageUrl: discardChanges,
                  showCancelButton: true,
                  confirmButtonText: "Confirm",
                  showLoaderOnConfirm: true,
                  reverseButtons: true,
                  buttonsStyling: false,
                  customClass: {
                    popup: "sw-popup",
                    htmlContainer: "sw-content",
                    confirmButton: "sw-btn-default sw-btn-confirm",
                    cancelButton: "sw-btn-default sw-btn-cancel",
                  },
                });
                if (!result.isConfirmed) return;

                const url = new URL(`/api/booking/${booking.id}/avaliable`, window.location);
                const res = await fetch(url, {
                  headers: { 'X-ACCESS-TOKEN': accessToken },
                  method: 'POST',
                });
                if (res.ok) {
                  Swal.fire({
                    icon: "success",
                    title: "Skipped cable removal successfully!",
                    showConfirmButton: false,
                    timer: 1500,
                  });
                  setUpdate(update => update + 1);
                } else {
                  notification["error"]({
                    message: "Something went wrong !",
                    description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
                  });
                }
              }}
            >
              Avaliable
            </Button>
          )}
          {(booking.status === 'stopped') && (
            <Button
              size="small"
              type="link"
              danger
              onClick={async () => {
                const result = calcPrice({
                  booking: {
                    ...booking,
                    startTime: booking.startTime && new Date(booking.startTime),
                    stopTime: booking.stopTime && new Date(booking.stopTime),
                    avaliableAt: booking.avaliableAt && new Date(booking.avaliableAt),
                    fullAt: booking.fullAt && new Date(booking.fullAt),
                  },
                  now: new Date(),
                });

                await Swal.fire({
                  title: `Set payment amount`,
                  html: `
                    <label for="swal-price-by-duration" class="text-center block pt-4">Price By Duration (THB)</label>
                    <input id="swal-price-by-duration" class="swal2-input" value="${result.priceByDuration.toFixed(2)}">
                    <label for="swal-price-by-power" class="text-center block">Price By Power (THB)</label>
                    <input id="swal-price-by-power" class="swal2-input" value="${result.priceByPower.toFixed(2)}">
                    <label for="swal-price-by-penelty" class="text-center block pt-4">Price By Penelty (THB)</label>
                    <input id="swal-price-by-penelty" class="swal2-input" value="${result.priceByPenalty.toFixed(2)}">
                  `,
                  showCancelButton: true,
                  confirmButtonText: "Confirm",
                  showLoaderOnConfirm: true,
                  reverseButtons: true,
                  async preConfirm() {
                    const priceByPower = document.getElementById("swal-price-by-power").value;
                    const priceByDuration = document.getElementById("swal-price-by-duration").value;
                    const priceByPenalty = document.getElementById("swal-price-by-penelty").value;

                    const url = new URL(`/api/booking/${booking.id}`, window.location);
                    const res = await fetch(url, {
                      headers: { 'X-ACCESS-TOKEN': accessToken },
                      method: 'PATCH',
                      body: JSON.stringify({
                        amount: +priceByPower + +priceByDuration + +priceByPenalty,
                        priceByPower: +priceByPower,
                        priceByDuration: +priceByDuration,
                        priceByPenalty: +priceByPenalty,
                      }),
                    });
                    if (res.ok) {
                      Swal.fire({
                        icon: "success",
                        title: "Set payment amount successfully!",
                        showConfirmButton: false,
                        timer: 1500,
                      });
                      setUpdate(update => update + 1);
                    } else {
                      notification["error"]({
                        message: "Something went wrong !",
                        description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
                      });
                    }
                  },
                });
              }}
            >
              Set amount
            </Button>
          )}
          {(booking.status === 'stopped') && (
            <Button
              size="small"
              type="link"
              danger
              onClick={async () => {
                const result = await Swal.fire({
                  title: `Manual payment`,
                  text: 'Are you sure to create manual payment?',
                  imageUrl: discardChanges,
                  showCancelButton: true,
                  confirmButtonText: "Confirm",
                  showLoaderOnConfirm: true,
                  reverseButtons: true,
                  buttonsStyling: false,
                  customClass: {
                    popup: "sw-popup",
                    htmlContainer: "sw-content",
                    confirmButton: "sw-btn-default sw-btn-confirm",
                    cancelButton: "sw-btn-default sw-btn-cancel",
                  },
                });
                if (!result.isConfirmed) return;

                const { price } = calcPrice({
                  booking: {
                    ...booking,
                    startTime: booking.startTime && new Date(booking.startTime),
                    stopTime: booking.stopTime && new Date(booking.stopTime),
                    avaliableAt: booking.avaliableAt && new Date(booking.avaliableAt),
                    fullAt: booking.fullAt && new Date(booking.fullAt),
                  },
                  now: new Date(),
                });

                const url = new URL(`/api/booking/${booking.id}/pay`, window.location);
                const res = await fetch(url, {
                  headers: { 'X-ACCESS-TOKEN': accessToken },
                  method: 'POST',
                  body: JSON.stringify({
                    amount: booking.adminAmount ?? price,
                  }),
                });
                if (res.ok) {
                  Swal.fire({
                    icon: "success",
                    title: "Created payment successfully!",
                    showConfirmButton: false,
                    timer: 1500,
                  });
                  setUpdate(update => update + 1);
                } else {
                  notification["error"]({
                    message: "Something went wrong !",
                    description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
                  });
                }
              }}
            >
              Pay
            </Button>
          )}
          {(booking.status === 'stopped') && (
            <Button
              size="small"
              type="link"
              danger
              onClick={async () => {
                const result = await Swal.fire({
                  title: `Cancel payment`,
                  text: 'Are you sure to cancel payment?',
                  imageUrl: discardChanges,
                  showCancelButton: true,
                  confirmButtonText: "Confirm",
                  showLoaderOnConfirm: true,
                  reverseButtons: true,
                  buttonsStyling: false,
                  customClass: {
                    popup: "sw-popup",
                    htmlContainer: "sw-content",
                    confirmButton: "sw-btn-default sw-btn-confirm",
                    cancelButton: "sw-btn-default sw-btn-cancel",
                  },
                });
                if (!result.isConfirmed) return;

                const url = new URL(`/api/booking/${booking.id}/cancel-payment`, window.location);
                const res = await fetch(url, {
                  headers: { 'X-ACCESS-TOKEN': accessToken },
                  method: 'POST',
                });
                if (res.ok) {
                  Swal.fire({
                    icon: "success",
                    title: "Cancelled payment successfully!",
                    showConfirmButton: false,
                    timer: 1500,
                  });
                  setUpdate(update => update + 1);
                } else {
                  notification["error"]({
                    message: "Something went wrong !",
                    description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
                  });
                }
              }}
            >
              Cancel
            </Button>
          )}
        </span>
      ),
    },
  ].filter(column => !column.hidden);

  return (
    <>
      <div className="flex justify-between items-center mt-2 space-x-4">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconBookingManagement /> &nbsp;&nbsp;Booking
            </span>
          }
        />
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Booking Id"
                  name="bookingId"
                  colon={false}
                  initialValue={null}
                >
                  <InputNumber
                    placeholder="Booking Id"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Charger Id"
                  name="chargerId"
                  colon={false}
                  initialValue={null}
                >
                  <InputNumber
                    placeholder="Charger Id"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Connector Id"
                  name="connectorId"
                  colon={false}
                  initialValue={null}
                >
                  <InputNumber
                    placeholder="Connector Id"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Name/Email/Tel"
                  name="userName"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Name/Email/Tel"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              {['admin', 'callcenter'].includes(adminRole) && (
                <Col className="gutter-row" span={4}>
                  <Form.Item
                    label="Cluster Name"
                    name="merchantIds"
                    colon={false}
                    initialValue={[]}
                  >
                    <MerchantDropdown
                      allowClear
                      allowSearch
                      mode="multiple"
                      setSelectedMerchants={(merchantIds) => form.setFieldsValue({ merchantIds })}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Site Name"
                  name="landlordIds"
                  colon={false}
                  initialValue={[]}
                >
                  <LandlordDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    merchantIds={selectedMerchants}
                    setSelectedLandlords={(landlordIds) => form.setFieldsValue({ landlordIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Payment Method"
                  name="paymentMethod"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    placeholder="Payment Method"
                    allowClear
                    options={[
                      { value: null, label: 'Any Payment Method' },
                      ...listPaymentMethod.map(type => ({
                        value: type.name,
                        label: type.value,
                      }))
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Service Date From"
                  name="createdAfter"
                  colon={false}
                  initialValue={null}
                >
                  <DatePicker
                    placeholder="Service Date From"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Service Date To"
                  name="createdBefore"
                  colon={false}
                  initialValue={null}
                >
                  <DatePicker
                    placeholder="Service Date To"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Booking Status"
                  name="status"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    placeholder="Booking Name"
                    allowClear
                    options={[
                      { value: null, label: 'Any Booking Status' },
                      ...listBookingStatus.map(type => ({
                        value: type.name,
                        label: type.value.text,
                      }))
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={24}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>

                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(booking) => booking.id}
          loading={loading}
          columns={columns}
          dataSource={bookings}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <DeleteAction
        openModalActionDelete={!!currentBooking}
        name="Booking"
        type="booking"
        editRecord={currentBooking}
        onCancel={() => setCurrentBooking(null)}
        onOk={() => {
          setCurrentBooking(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default Booking;
